import { inferRouteObject } from 'react-router-typed-object';

import { Navigate } from '@/js/libs/router';
import { or, PrivateRoute, Roles } from '@/js/libs/use-auth';

import type { Condition } from 'src/components/RoleBasedRedirector.tsx';
import RoleBasedRedirector from 'src/components/RoleBasedRedirector.tsx';
import RouteParamsValidator from 'src/components/RouteParamsValidator.tsx';
import { BroadcastRoleChecker } from 'src/scenes/ProductCatalogue/containers/BroadcastProducts/components/BroadcastRoleChecker.tsx';
import { validateBroadcastType } from 'src/scenes/ProductCatalogue/containers/BroadcastProducts/helpers.ts';
import { BroadcastUrlTypes } from 'src/scenes/ProductCatalogue/containers/BroadcastProducts/types.ts';
import { validateDigitalProductRouteIds } from 'src/scenes/ProductCatalogue/containers/DigitalProducts/helpers.ts';
import ProductCatalogue from 'src/scenes/ProductCatalogue/index.ts';
import Status from 'src/scenes/Status/index.ts';
import { productCatalogueRouteRoles } from './sectionRoles.ts';

const index = true;

const productCatalogueConditions: Condition[] = [
  { roles: Roles.MANAGE_DIGITAL_PRODUCTS, to: './digital-products' },
  { roles: Roles.MANAGE_POS_PRODUCTS, to: './point-of-sale-products' },
  { roles: Roles.MANAGE_CONSULTING_PRODUCTS, to: './consulting-products' },
  { roles: Roles.MANAGE_PRINT_PRODUCTS, to: './print-products' },
  { roles: Roles.MANAGE_RADIO_PRODUCTS, to: `./${BroadcastUrlTypes.RADIO}` },
  { roles: Roles.MANAGE_TV_PRODUCTS, to: `./${BroadcastUrlTypes.TV}` },
  { roles: Roles.POSTNETZ, to: './analog-products' },
];

export const productCatalogueRouteObject = inferRouteObject({
  path: 'product-catalogue',
  element: <PrivateRoute roles={productCatalogueRouteRoles} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: ProductCatalogue.Helmet,
      children: [
        {
          index,
          element: <RoleBasedRedirector conditions={productCatalogueConditions} noAccessElement={<Status.NotFound />} />,
        },
        {
          path: 'digital-products',
          element: <PrivateRoute roles={Roles.MANAGE_DIGITAL_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              element: <RouteParamsValidator validate={validateDigitalProductRouteIds} invalidStateElement={<Status.NotFound />} />,
              children: [
                { path: 'item/:id?', Component: ProductCatalogue.DigitalProducts.Products.Edit },
                { path: 'history/:entityId', Component: ProductCatalogue.DigitalProducts.Products.History },
                { path: 'ad-media/:mode/:id?', Component: ProductCatalogue.DigitalProducts.AdMediaForm },
                { path: 'ad-media/history/:entityId', Component: ProductCatalogue.DigitalProducts.AdMediaHistory },
                { path: 'price/:id?', Component: ProductCatalogue.DigitalProducts.AdPrice.Edit },
                { path: 'price/history/:entityId', Component: ProductCatalogue.DigitalProducts.AdPrice.History },
                { path: 'ad-type/:id?', Component: ProductCatalogue.DigitalProducts.AdTypes.Edit },
                { path: 'ad-type/history/:entityId', Component: ProductCatalogue.DigitalProducts.AdTypes.History },
                { path: 'placement/:id?', Component: ProductCatalogue.DigitalProducts.Placement.Edit },
                { path: 'placement/history/:entityId', Component: ProductCatalogue.DigitalProducts.Placement.History },
              ],
            },
            {
              Component: ProductCatalogue.DigitalProducts.Layout,
              children: [
                { index, Component: ProductCatalogue.DigitalProducts.Products.List },
                { path: 'ad-media', Component: ProductCatalogue.DigitalProducts.AdMediaList },
                { path: 'placements', Component: ProductCatalogue.DigitalProducts.Placement.List },
                { path: 'ad-types', Component: ProductCatalogue.DigitalProducts.AdTypes.List },
                { path: 'prices', Component: ProductCatalogue.DigitalProducts.AdPrice.List },
                {
                  path: 'admedium-mapping',
                  children: [
                    { index, Component: ProductCatalogue.DigitalProducts.AdMediumMapping.Products },
                    {
                      path: 'edit-mapping',
                      Component: ProductCatalogue.DigitalProducts.AdMediumMapping.Mappings,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: ':broadcastType',
          element: <RouteParamsValidator validate={validateBroadcastType} invalidStateElement={<Status.NotFound />} />,
          children: [
            {
              Component: BroadcastRoleChecker,
              children: [
                { index, Component: ProductCatalogue.BroadcastProducts.Products },
                { path: 'create', Component: ProductCatalogue.BroadcastProducts.ProductForm },
                { path: 'edit/:productId', Component: ProductCatalogue.BroadcastProducts.ProductForm },
                { path: 'history/:entityId', Component: ProductCatalogue.BroadcastProducts.ProductHistory },

                {
                  path: 'ad-media',
                  children: [
                    { index, Component: ProductCatalogue.BroadcastProducts.AdMedia },
                    { path: 'history/:entityId', Component: ProductCatalogue.BroadcastProducts.AdMediaHistory },
                  ],
                },
                {
                  path: 'placements',
                  children: [
                    { index, Component: ProductCatalogue.BroadcastProducts.AdPlacements },
                    { path: 'create', Component: ProductCatalogue.BroadcastProducts.AdPlacementForm },
                    { path: 'edit/:placementId', Component: ProductCatalogue.BroadcastProducts.AdPlacementForm },
                    { path: 'history/:entityId', Component: ProductCatalogue.BroadcastProducts.AdPlacementHistory },
                  ],
                },

                {
                  path: 'ad-types',
                  children: [
                    { index, Component: ProductCatalogue.BroadcastProducts.AdTypes },
                    { path: 'history/:entityId', Component: ProductCatalogue.BroadcastProducts.AdTypeHistory },
                  ],
                },
                {
                  path: 'ad-prices',
                  children: [
                    { index, Component: ProductCatalogue.BroadcastProducts.AdPrices },
                    { path: 'create', Component: ProductCatalogue.BroadcastProducts.CreateAdPriceForm },
                    { path: 'edit/:priceId', Component: ProductCatalogue.BroadcastProducts.EditAdPriceForm },
                    { path: 'history/:entityId', Component: ProductCatalogue.BroadcastProducts.AdPriceHistory },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'position-groups',
          element: (
            <PrivateRoute
              roles={or(
                Roles.MANAGE_DIGITAL_PRODUCTS,
                Roles.MANAGE_PRINT_PRODUCTS,
                Roles.MANAGE_POS_PRODUCTS,
                Roles.MANAGE_CONSULTING_PRODUCTS,
              )}
              noAccessComponent={Status.NotFound}
            />
          ),
          children: [
            { index, Component: ProductCatalogue.PositionGroups.PositionGroupsList },
            { path: 'create', Component: ProductCatalogue.PositionGroups.PositionGroupForm },
            { path: 'edit/:positionGroupId', Component: ProductCatalogue.PositionGroups.PositionGroupForm },
            { path: 'edit-positions/:positionGroupId', Component: ProductCatalogue.PositionGroups.PositionsForm },
          ],
        },
        {
          path: 'print-products',
          element: <PrivateRoute roles={Roles.MANAGE_PRINT_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'placements/:itemId', Component: ProductCatalogue.PrintProducts.Placements.Form },
            { path: 'placements/create', Component: ProductCatalogue.PrintProducts.Placements.Form },
            { path: 'placements/history/:entityId', Component: ProductCatalogue.PrintProducts.Placements.PlacementHistory },
            { path: ':itemId', Component: ProductCatalogue.PrintProducts.Products.Form },
            { path: 'create', Component: ProductCatalogue.PrintProducts.Products.Form },
            { path: 'history/:entityId', Component: ProductCatalogue.PrintProducts.Products.ProductHistory },
            { path: 'ad-prices/:itemId', Component: ProductCatalogue.PrintProducts.AdPrices.Form },
            { path: 'ad-prices/create', Component: ProductCatalogue.PrintProducts.AdPrices.Form },
            { path: 'ad-prices/history/:entityId', Component: ProductCatalogue.PrintProducts.AdPrices.AdPriceHistory },
            { path: 'calendars/:itemId', Component: ProductCatalogue.PrintProducts.Calendars.Form },
            { path: 'calendars/create', Component: ProductCatalogue.PrintProducts.Calendars.Form },
            { path: 'ad-types/history/:entityId', Component: ProductCatalogue.PrintProducts.AdTypes.AdTypeHistory },
            { path: 'ad-medias/history/:entityId', Component: ProductCatalogue.PrintProducts.AdMedias.AdMediaHistory },
            {
              Component: ProductCatalogue.PrintProducts.Layout,
              children: [
                { index, Component: ProductCatalogue.PrintProducts.Products.List },
                { path: 'ad-medias', Component: ProductCatalogue.PrintProducts.AdMedias.List },
                { path: 'placements', Component: ProductCatalogue.PrintProducts.Placements.List },
                { path: 'ad-types', Component: ProductCatalogue.PrintProducts.AdTypes.List },
                { path: 'ad-prices', Component: ProductCatalogue.PrintProducts.AdPrices.List },
                { path: 'calendars', Component: ProductCatalogue.PrintProducts.Calendars.List },
                { path: 'size-configs', Component: ProductCatalogue.PrintProducts.SizeConfigs.List },
                { path: 'availabilities', Component: ProductCatalogue.PrintProducts.Availabilities.List },
              ],
            },
          ],
        },
        {
          path: 'point-of-sale-products',
          element: <PrivateRoute roles={Roles.MANAGE_POS_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            { path: 'create', Component: ProductCatalogue.PointOfSaleProducts.ProductForm },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                { path: ':id', Component: ProductCatalogue.PointOfSaleProducts.ProductForm },
              ],
            },
            { path: 'ad-prices/create', Component: ProductCatalogue.PointOfSaleProducts.AdPriceForm },
            {
              path: 'ad-prices/edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                { path: ':id', Component: ProductCatalogue.PointOfSaleProducts.AdPriceForm },
              ],
            },
            { path: 'history/:entityId', Component: ProductCatalogue.PointOfSaleProducts.ProductHistory },
            { path: 'ad-media/history/:entityId', Component: ProductCatalogue.PointOfSaleProducts.AdMediaHistory },
            { path: 'placements/history/:entityId', Component: ProductCatalogue.PointOfSaleProducts.PlacementHistory },
            { path: 'ad-types/history/:entityId', Component: ProductCatalogue.PointOfSaleProducts.AdTypeHistory },
            { path: 'ad-prices/history/:entityId', Component: ProductCatalogue.PointOfSaleProducts.AdPriceHistory },
            {
              Component: ProductCatalogue.PointOfSaleProducts.Layout,
              children: [
                { index, Component: ProductCatalogue.PointOfSaleProducts.Products },
                { path: 'ad-media', Component: ProductCatalogue.PointOfSaleProducts.AdMedia },
                { path: 'placements', Component: ProductCatalogue.PointOfSaleProducts.Placements },
                { path: 'ad-types', Component: ProductCatalogue.PointOfSaleProducts.AdTypes },
                { path: 'ad-prices', Component: ProductCatalogue.PointOfSaleProducts.AdPrices },
              ],
            },
          ],
        },
        {
          path: 'consulting-products',
          element: <PrivateRoute roles={Roles.MANAGE_CONSULTING_PRODUCTS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              Component: ProductCatalogue.ConsultingProducts.Helmet,
              children: [
                { path: 'create', Component: ProductCatalogue.ConsultingProducts.ProductForm },
                {
                  path: 'edit',
                  children: [
                    { index, element: <Navigate to='../' replace /> },
                    { path: ':id', Component: ProductCatalogue.ConsultingProducts.ProductForm },
                  ],
                },
                { path: 'ad-prices/create', Component: ProductCatalogue.ConsultingProducts.AdPriceForm },
                {
                  path: 'ad-prices/edit',
                  children: [
                    { index, element: <Navigate to='../' replace /> },
                    { path: ':id', Component: ProductCatalogue.ConsultingProducts.AdPriceForm },
                  ],
                },
                { path: 'history/:entityId', Component: ProductCatalogue.ConsultingProducts.ProductHistory },
                { path: 'ad-media/history/:entityId', Component: ProductCatalogue.ConsultingProducts.AdMediaHistory },
                { path: 'placements/history/:entityId', Component: ProductCatalogue.ConsultingProducts.PlacementHistory },
                { path: 'ad-types/history/:entityId', Component: ProductCatalogue.ConsultingProducts.AdTypeHistory },
                { path: 'ad-prices/history/:entityId', Component: ProductCatalogue.ConsultingProducts.AdPriceHistory },
                {
                  Component: ProductCatalogue.ConsultingProducts.Layout,
                  children: [
                    { index, Component: ProductCatalogue.ConsultingProducts.ProductsList },
                    { path: 'ad-media', Component: ProductCatalogue.ConsultingProducts.AdMediaList },
                    { path: 'placements', Component: ProductCatalogue.ConsultingProducts.PlacementsList },
                    { path: 'ad-types', Component: ProductCatalogue.ConsultingProducts.AdTypesList },
                    { path: 'ad-prices', Component: ProductCatalogue.ConsultingProducts.AdPricesList },
                  ],
                },
              ],
            },
          ],
        },
        {
          element: (
            <PrivateRoute
              roles={or(
                Roles.MANAGE_DIGITAL_PRODUCTS,
                Roles.MANAGE_PRINT_PRODUCTS,
                Roles.MANAGE_TV_PRODUCTS,
                Roles.MANAGE_RADIO_PRODUCTS,
                Roles.MANAGE_POS_PRODUCTS,
                Roles.MANAGE_CONSULTING_PRODUCTS,
              )}
              noAccessComponent={Status.NotFound}
            />
          ),
          children: [{ path: 'price-lists', Component: ProductCatalogue.PriceLists }],
        },
        {
          path: 'analog-products',
          element: <PrivateRoute roles={Roles.POSTNETZ} noAccessComponent={Status.NotFound} />,
          children: [
            { index, element: <Navigate to='./advertisements' replace /> },
            {
              Component: ProductCatalogue.AnalogProducts.Layout,
              children: [
                {
                  path: 'advertisements',
                  children: [
                    { path: ':advertisementId', Component: ProductCatalogue.AnalogProducts.AdvertisementsEdit },
                    { index, Component: ProductCatalogue.AnalogProducts.Advertisements },
                  ],
                },
                { path: 'advertising-spaces', Component: ProductCatalogue.AnalogProducts.AdvertisingSpaces },
                { path: 'branch-cluster-factors', Component: ProductCatalogue.AnalogProducts.BranchClusters },
                { path: 'branch-count-factors', Component: ProductCatalogue.AnalogProducts.BranchCountFactors },
                { path: 'dispatches', Component: ProductCatalogue.AnalogProducts.Dispatches },
                { path: 'monthly-factors', Component: ProductCatalogue.AnalogProducts.MonthlyFactors },
              ],
            },
            {
              path: 'position-groups',
              children: [
                { index, Component: ProductCatalogue.AnalogProducts.PositionGroups.List },
                { path: 'create', Component: ProductCatalogue.AnalogProducts.PositionGroups.PositionGroupForm },
                {
                  path: ':groupId',
                  children: [
                    { index, Component: ProductCatalogue.AnalogProducts.PositionGroups.PositionGroupForm },
                    { path: 'positions', Component: ProductCatalogue.AnalogProducts.PositionGroups.PositionsForm },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});
